import { useTheme } from "@emotion/react";
import {
	Autocomplete,
	Button,
	TextField,
	Grid,
	useMediaQuery,
	InputAdornment,
	IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import debounce from "lodash.debounce";
import React, { useCallback, useState } from "react";
import { renderCodeOption } from "../../utils/functions";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import {
	formatSaveDateFrom,
	formatSaveDateTo,
} from "../../reducers/TimeReducer";
import { Clear } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const MaintenanceTableToolbar = ({
	openAdd,
	search,
	setSearch,
	onFilterChange,
	filters,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const btnStyles = {
		color: theme.palette.mode === "dark" ? "#fff" : "primary",
		borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
		"&:hover": {
			borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
			backgroundColor: theme.palette.action.hover,
		},
	};

	const [showAdvSearch, setShowAdvSearch] = useState(false);
	const [localFilters, setLocalFilters] = useState(filters);
	const [advancedActive, setAdvancedActive] = useState(false);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchChange = useCallback(
		debounce((searchString) => {
			setSearch(searchString);
		}, 500),
		[onFilterChange],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange],
	);

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters],
	);

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : type === "date" ? (
						<DatePicker
							label={label}
							value={
								localFilters[field]
									? moment(localFilters[field])
									: null
							}
							onChange={(date) =>
								handleFilterChange(
									field,
									date
										? field === "from"
											? formatSaveDateFrom(date)
											: formatSaveDateTo(date)
										: null,
								)
							}
							minDate={
								field === "to" && localFilters.from
									? moment(localFilters.from)
									: undefined
							}
							maxDate={
								field === "from" && localFilters.to
									? moment(localFilters.to)
									: undefined
							}
							clearable
							clearText="Clear"
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									size="small"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{localFilters[field] && (
													<InputAdornment position="end">
														<IconButton
															onClick={() =>
																handleFilterChange(
																	field,
																	null,
																)
															}
															size="small"
															edge="end"
														>
															<Clear fontSize="small" />
														</IconButton>
													</InputAdornment>
												)}
												{
													params.InputProps
														?.endAdornment
												}
											</>
										),
									}}
								/>
							)}
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							renderOption={renderCodeOption}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
										}
									: {}
							}
						/>
					)}
				</Grid>
			),
		);

	const filterFields = [
		{
			type: "autocomplete",
			label: "Status",
			field: "status",
			options: [],
			xs: 1,
		},
	];

	const advanceFilterFields = [
		{
			type: "autocomplete",
			label: "Equipment",
			field: "equipmentID",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => v.make)).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Perform By",
			field: "performBy",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => v.model)).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Assign To",
			field: "perPersonnelID",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => v.model)).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Funding",
			field: "fundingSourceID",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => v.model)).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1.2,
		},
	];

	const advancedFilterDateFields = [
		{
			type: "autocomplete",
			label: "Period",
			field: "period",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => v.make)).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1,
		},
		{
			type: "date",
			label: "From Date",
			field: "from",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => v.model)).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "date",
			label: "To Date",
			field: "to",
			options: [
				// { value: "", label: "— None —" },
				// ...uniq(vehicles.map((v) => String(v.year || ""))).map((v) => ({
				// 	value: v,
				// 	label: v,
				// })),
			],
			multiselect: true,
			xs: 1.2,
		},
	];

	const clearAdvanced = () => {};

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Maintenance Records</div>
			</div>
			<div className="section-actions mb-2">
				<TextField
					label="Search description, perform by..."
					type="search"
					className={classes.filter}
					variant="outlined"
					size="small"
					style={{
						width: "100%",
						maxWidth: 250,
					}}
					value={search}
					onChange={(e) => debouncedSearchChange(e.target.value)}
				/>
				{renderFilterFields(filterFields)}
				<Button
					variant="outlined"
					color="primary"
					sx={btnStyles}
					onClick={() => setShowAdvSearch((s) => !s)}
				>
					🔍 Advanced
					{advancedActive && (
						<span
							style={{
								marginLeft: 4,
								background: "rgba(255,255,255,0.3)",
								borderRadius: 99,
								fontSize: 10,
								fontWeight: 700,
								padding: "1px 5px",
							}}
						>
							ON
						</span>
					)}
				</Button>
				<Button
					variant="outlined"
					color="primary"
					// onClick={() => printVehicleList()}
					title="Print current list"
					sx={btnStyles}
				>
					🖨️ Print
				</Button>
				<Button variant="contained" color="primary" onClick={openAdd}>
					+ New Record
				</Button>
			</div>
			{showAdvSearch && (
				<div
					style={{
						margin: "8px",
						padding: "14px 16px",
						background: "var(--surface)",
						border: "1px solid var(--border)",
						borderRadius: "8px",
						display: "flex",
						flexWrap: "wrap",
						gap: 8,
						alignItems: "center",
					}}
				>
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
						}}
					>
						Filter by:
					</span>
					{renderFilterFields([...advanceFilterFields])}
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
						}}
					>
						Date:
					</span>
					{renderFilterFields([...advancedFilterDateFields])}
					{advancedActive && (
						<button
							className="btn btn-ghost btn-sm"
							style={{ marginLeft: "auto", fontSize: 11 }}
							onClick={clearAdvanced}
						>
							✕ Clear all filters
						</button>
					)}
				</div>
			)}
		</>
	);
};

export default MaintenanceTableToolbar;
